import html2canvas from 'html2canvas';

/**
 * DOM 요소를 이미지로 캡처
 * @param elementId - 캡처할 요소의 ID
 * @returns Base64 인코딩된 PNG 이미지 데이터 URL, 실패 시 null
 */
export const captureElementAsImage = async (elementId: string): Promise<string | null> => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      console.warn(`Element with id "${elementId}" not found for capture.`);
      return null;
    }
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      imageTimeout: 0,
    });
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error(`Error capturing element "${elementId}":`, error);
    return null;
  }
};

/**
 * Canvas 요소를 고해상도 PNG 이미지로 변환
 * @param canvas - 캡처할 Canvas 요소
 * @param scale - 해상도 배율 (기본값: 3, 300 DPI 상당)
 * @returns Base64 인코딩된 PNG 이미지 데이터 URL
 */
export const captureCanvasAsHighResImage = (canvas: HTMLCanvasElement, scale: number = 3): string => {
  // 원본 캔버스 크기 로그 (디버깅용)
  // console.log('🖼️ Canvas 캡처:', {
  //   originalWidth: canvas.width,
  //   originalHeight: canvas.height,
  //   scale: scale,
  //   targetWidth: canvas.width * scale,
  //   targetHeight: canvas.height * scale
  // });

  // 임시 고해상도 캔버스 생성
  const tempCanvas = document.createElement('canvas');
  const ctx = tempCanvas.getContext('2d');

  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  // 고해상도로 캔버스 크기 설정
  tempCanvas.width = canvas.width * scale;
  tempCanvas.height = canvas.height * scale;

  // 캔버스 완전 초기화 (중복 방지)
  ctx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

  // 스케일 적용하여 원본 캔버스를 고해상도로 그리기
  // drawImage(source, dx, dy, dWidth, dHeight) 형식 사용
  ctx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);

  // PNG 형식으로 변환 (압축 없음, 최고 품질)
  return tempCanvas.toDataURL('image/png');
};

/**
 * 습공기 선도 데이터 인터페이스
 */
export interface PsychrometricChartData {
  image: string; // Base64 PNG 이미지
  points: any[]; // 상태점 정보
  lines: any[];  // 프로세스 라인 정보
  metadata: {
    capturedAt: string; // 캡처 시간
    elevation: number;  // 고도
    saFlow: number;     // 급기량
    raFlow: number;     // 환기량
    oaFlow: number;     // 외기량
    eaFlow: number;     // 배기량
    [key: string]: any; // 추가 메타데이터
  };
}

/**
 * Canvas와 상태점 데이터를 함께 저장
 * @param canvas - 습공기 선도 Canvas 요소
 * @param points - 상태점 배열
 * @param lines - 프로세스 라인 배열
 * @param metadata - 메타데이터
 * @param scale - 이미지 해상도 배율 (기본값: 3)
 * @returns 습공기 선도 데이터 객체
 */
export const capturePsychrometricChart = (
  canvas: HTMLCanvasElement,
  points: any[],
  lines: any[],
  metadata: Partial<PsychrometricChartData['metadata']>,
  scale: number = 3
): PsychrometricChartData => {
  const image = captureCanvasAsHighResImage(canvas, scale);

  return {
    image,
    points: JSON.parse(JSON.stringify(points)), // Deep copy
    lines: JSON.parse(JSON.stringify(lines)),   // Deep copy
    metadata: {
      capturedAt: new Date().toISOString(),
      elevation: 0,
      saFlow: 0,
      raFlow: 0,
      oaFlow: 0,
      eaFlow: 0,
      ...metadata,
    },
  };
};
